import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Icon } from '../theme/Icon';
import s from './SearchCombobox.module.css';
import { t } from '@app/i18n';

export interface ComboOption {
  value: string;
  label: string;
  /** Segunda linha da opção (slug, namespace, apim_id). */
  hint?: string;
  /** `true` = `label`/`hint` em monoespaçada (identificador técnico, FR-133). */
  mono?: boolean;
  disabled?: boolean;
  /** `danger` = o `hint` é o **motivo** de a opção estar desabilitada — em vermelho. */
  hintTone?: 'danger';
}

export interface SearchComboboxProps {
  options: ComboOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Rótulo acessível — passe quando não houver `<label htmlFor>` (use `id`). */
  label?: string;
  id?: string;
  placeholder?: string;
  /** Texto do campo de busca dentro do painel. */
  searchPlaceholder?: string;
  disabled?: boolean;
  size?: 'md' | 'sm';
  invalid?: boolean;
  /** Habilita "+ criar «texto»" quando nada corresponde (FR-129). */
  creatable?: boolean;
  /** Chamado ao escolher "+ criar «texto»". */
  onCreate?: (texto: string) => void;
  /** Mensagem quando o filtro não casa com nada. */
  emptyMessage?: string;
  /**
   * Mostra um «×» ao lado do valor escolhido que volta para `null` sem abrir o
   * painel — é como um filtro de grade se limpa sozinho, sem o «Limpar filtros»
   * (ajustes-3, 28/08, item 15.1). Fora de filtros fica desligado: num
   * formulário o campo vazio é erro, não estado.
   */
  clearable?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
  className?: string;
}

/**
 * Combobox pesquisável (FR-129) — **nunca** um `<select>` nativo.
 *
 * Comportamento: painel vazio lista tudo; digitar filtra; sem correspondência
 * mostra "nenhuma opção corresponde" e, com `creatable`, "+ criar «texto»".
 * Teclado: Enter/Espaço/↓ abre, ↑/↓ navega, Enter escolhe, Esc fecha, Tab fecha.
 * ARIA: `combobox` + `listbox` com `aria-activedescendant`.
 */
export function SearchCombobox({
  options,
  value,
  onChange,
  label,
  id,
  placeholder = 'Selecione…',
  searchPlaceholder = t('common.ui.search'),
  disabled,
  size = 'md',
  invalid,
  creatable = false,
  onCreate,
  emptyMessage = t('common.ui.emptyOption'),
  clearable = false,
  className,
  ...aria
}: SearchComboboxProps) {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const listId = `${triggerId}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const showCreate = creatable && query.trim().length > 0 && filtered.length === 0;
  // O item "criar" ocupa o índice 0 quando aparece.
  const itemCount = filtered.length + (showCreate ? 1 : 0);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const i = value
      ? Math.max(
          0,
          options.findIndex((o) => o.value === value),
        )
      : 0;
    setActiveIndex(i);
  }, [disabled, options, value]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Clique fora fecha (sem devolver o foco, para não brigar com o próximo alvo).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  const pick = useCallback(
    (index: number) => {
      if (showCreate && index === 0) {
        onCreate?.(query.trim());
        close();
        return;
      }
      const option = filtered[showCreate ? index - 1 : index];
      if (!option || option.disabled) return;
      onChange(option.value);
      close();
    },
    [showCreate, onCreate, query, filtered, onChange, close],
  );

  const onKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPanel();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (itemCount === 0 ? 0 : (i + 1) % itemCount));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (itemCount === 0 ? 0 : (i - 1 + itemCount) % itemCount));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(Math.max(0, itemCount - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (itemCount > 0) pick(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  };

  /**
   * Teclado da própria opção. No fluxo normal o foco fica no gatilho/na busca e a
   * opção corrente é apontada por `aria-activedescendant` (padrão APG), mas a linha
   * é focável por programa (`tabIndex={-1}`) — leitor de tela em modo formulário e
   * `focus()` levam o foco até ela. Então ela responde ao mesmo teclado da lista:
   * Enter/Espaço escolhem, as demais teclas seguem para a navegação.
   */
  const onOptionKeyDown = (index: number) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(index);
      return;
    }
    onKeyDown(e);
  };

  const optionId = (index: number) => `${listId}-opt-${index}`;

  return (
    <div
      ref={wrapRef}
      className={[
        s.wrap,
        size === 'sm' ? s.sm : '',
        invalid ? s.invalid : '',
        clearable && selected && !disabled ? s.comLimpar : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className={s.trigger}
        // Fechado, o gatilho **é** o combobox do formulário — é nele que o foco
        // para ao tabular. Aberto, o papel passa para o campo de busca, que é
        // quem tem o foco: `aria-activedescendant` só é lido no elemento focado.
        role={open ? undefined : 'combobox'}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onKeyDown}
        {...aria}
      >
        <span
          className={[
            s.triggerValue,
            selected ? '' : s.placeholder,
            selected?.mono ? s.optMono : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="chevron-down" size="sm" aria-hidden="true" />
      </button>
      {/* Irmão do gatilho, não filho: botão dentro de botão não é HTML válido e
          o clique abriria o painel junto. */}
      {clearable && selected && !disabled ? (
        <button
          type="button"
          className={s.clear}
          aria-label={t('common.ui.clear', { label: label ?? placeholder })}
          title={t('common.ui.clear', { label: label ?? placeholder })}
          onClick={() => {
            onChange(null);
            close(false);
          }}
        >
          <Icon name="close" size="xs" aria-hidden="true" />
        </button>
      ) : null}

      {open ? (
        <div className={s.panel}>
          <div className={s.searchRow}>
            <Icon name="search" size="sm" aria-hidden="true" />
            <input
              ref={searchRef}
              className={s.search}
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              // O painel aberto é o combobox: este campo tem o foco, então é aqui
              // que o leitor de tela lê a opção corrente (padrão APG).
              role="combobox"
              aria-expanded="true"
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-controls={listId}
              aria-activedescendant={itemCount > 0 ? optionId(activeIndex) : undefined}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
            />
          </div>

          <ul
            className={s.options}
            id={listId}
            role="listbox"
            aria-label={label ?? t('common.ui.options')}
          >
            {showCreate ? (
              <li
                id={optionId(0)}
                role="option"
                aria-selected={false}
                className={[s.opt, s.create, activeIndex === 0 ? s.optActive : '']
                  .filter(Boolean)
                  .join(' ')}
                tabIndex={-1}
                onMouseEnter={() => setActiveIndex(0)}
                onClick={() => pick(0)}
                onKeyDown={onOptionKeyDown(0)}
              >
                <Icon name="plus" size="sm" aria-hidden="true" />
                <span className={s.optText}>{`criar «${query.trim()}»`}</span>
              </li>
            ) : null}

            {filtered.map((option, i) => {
              const index = showCreate ? i + 1 : i;
              const isSelected = option.value === value;
              return (
                <li
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  className={[
                    s.opt,
                    index === activeIndex ? s.optActive : '',
                    isSelected ? s.optSelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(index)}
                  onKeyDown={onOptionKeyDown(index)}
                >
                  <span
                    className={[s.optText, option.mono ? s.optMono : ''].filter(Boolean).join(' ')}
                  >
                    <span className={s.optLabel}>{option.label}</span>
                    {/* O `{' '}` NÃO é enfeite e não se apaga. Rótulo e dica são dois
                        elementos `display:block`, e o JSX descarta a linha só-de-espaço
                        entre eles: sem isto o `textContent` da opção cola —
                        «cobrancaspecialist · dc-rio» — e é isso que alguém copia e que
                        um leitor de tela pode ler. Na tela nada muda: `.optText` é um
                        ITEM de flex, não um container, então os filhos são blocos e um
                        nó só de espaço entre blocos não é renderizado.

                        DENTRO do ramo da dica, e não antes dele: fora, a opção SEM dica
                        ficava com um espaço à direita («cobranca ») — o teste «filtra por
                        rótulo e por dica» apanhou-o, porque compara o `textContent` exato.
                        O separador pertence à dica, não ao rótulo.

                        Terceira aparição desta família (o `<h2>` do login, 10/09).
                        Guardado pelo teste «a opção não cola rótulo e dica», que afirma
                        a JUNÇÃO — cada metade passa sozinha, e é isso que deixa passar. */}
                    {option.hint ? (
                      <>
                        {' '}
                        <span
                          className={[
                            s.optHint,
                            option.hintTone === 'danger' ? s.optHintDanger : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          data-tone={option.hintTone}
                        >
                          {option.hint}
                        </span>
                      </>
                    ) : null}
                  </span>
                  {isSelected ? <Icon name="check" size="sm" aria-hidden="true" /> : null}
                </li>
              );
            })}

            {itemCount === 0 ? (
              <li className={s.empty} role="presentation">
                {emptyMessage}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
